import {AbstractContactModel, ContactType, StatusColor} from './abstract-contact.model';
import {BuilderFactory} from '../shared/utils/builder.factory';
import {ServiceNumberData} from '@gt-api';
import {AgentStateTransition, SkillEvent} from '../services/push-event/push-events';
import {ServiceNumberUtil} from './service-number.util';
import {GtPhoneNumberUtil} from '../shared/utils/gt-phone-number.util';
import {GtServiceNumbersUtil} from '../shared/utils/gt-service-numbers.util';
import {QueueAgentModel, QueueDataModel} from './queue-data.model';

export class ServiceNumberModel extends AbstractContactModel {
  backendModel: ServiceNumberData;
  queueData: QueueDataModel[];
  pattern: string;
  transferDisplayExpression: string;

  private static sortMembers(members: QueueAgentModel[]): Array<QueueAgentModel> {
    members.sort((a, b) => {
      return a.skillLevel - b.skillLevel;
    });
    return members;
  }

  static convertServiceNumberData(model: ServiceNumberData): ServiceNumberModel {
    if (model.queueData) {
      model.queueData.forEach(qd => {
        ServiceNumberModel.sortMembers(qd.queueAgents);
      });
    }
    const statusText = '';
    const statusIcon = '';
    const statusColor = StatusColor.STATUS_GREEN;

    const serviceNumberData = BuilderFactory.createBuilder(ServiceNumberModel)
      .backendModel(model)
      .type(ContactType.ServiceNumber)
      .id(-1)
      .status(statusText)
      .statusIcon(statusIcon)
      .statusColor(statusColor)
      .name(model.serviceNumber.name)
      .pattern(model.serviceNumber.pattern)
      .phoneNumber(model.serviceNumber.number)
      .queueData(model.queueData.map(QueueDataModel.fromQueueData))
      .build();

    serviceNumberData.queueData.forEach(queueData => {
      queueData.pattern = serviceNumberData.pattern;
      serviceNumberData.updateQueueStatus(queueData);
    });
    return serviceNumberData;
  }

  getBackendModel(): ServiceNumberData {
    return this.backendModel as ServiceNumberData;
  }

  getMainQueue(): QueueDataModel {
    // return the queue which is used to set the state on the serviceNumber-entry in the contact-list
    return ServiceNumberUtil.filterCallbackQueues(this.queueData)[0];
  }

  handleSkillEvent(event: SkillEvent) {
    const queueData = this.queueData.find(q => q.cid === event.queue_cid);
    if (queueData) {
      switch (event.pushEventType) {
        case 'SkillChanged':
          const queueAgent: QueueAgentModel = queueData.queueAgents.find(skill => skill.login === event.login);
          if (queueAgent) {
            queueAgent.active = !event.deactivated;
            queueAgent.noReplyTime = event.no_reply_time;
            queueAgent.skillLevel = event.skill_level;
            queueAgent.wrapupTime = event.wrapup_time;
          }
          ServiceNumberModel.sortMembers(queueData.queueAgents);
          break;
        case 'SkillRemoved':
          queueData.queueAgents = queueData.queueAgents.filter(qa => qa.login !== event.login);
          break;
      }
      this.updateQueueStatus(queueData);
    }
  }

  handleAgentStateTransition(event: AgentStateTransition): boolean {
    let handled = false;
    this.queueData.forEach(queueData => {
      queueData.queueAgents.forEach((qa: QueueAgentModel) => {
        if (qa.login === event.agent_login) {
          qa.state = event.new_state;
          this.updateQueueStatus(queueData);
          handled = true;
        }
      });
    });
    return handled;
  }

  hasSkill(oname: string, login: string): boolean {
    return this.queueData.some(queueData => {
      return queueData.queueAgents.some(qa => qa.login === login);
    });
  }

  getFormattedNumber(): string {
    if (this.getBackendModel()) {
      if (GtServiceNumbersUtil.numberShouldBeVisible(this)) {
        return GtPhoneNumberUtil.toDisplay(this.phoneNumber);
      } else {
        return '';
      }
    } else {
      return GtPhoneNumberUtil.toDisplay(this.phoneNumber);
    }
  }

  private updateQueueStatus(queueData: QueueDataModel) {
    if (queueData) {
      queueData.agentsAvailable = queueData.queueAgents.filter(qa => qa.active && qa.state === 1).length;
      queueData.agentsLoggedIn = queueData.queueAgents.filter(qa => qa.active && qa.state > 0).length;
    }
    this.updateQueueStatusText();
  }

  private updateQueueStatusText() {
    const queueData = this.getMainQueue();
    if (queueData) {
      if (queueData.queueAgents.length) {
        this.status = queueData.agentsAvailable + '/' + queueData.queueAgents.length;
        if (queueData.agentsAvailable === 0) {
          this.statusColor = StatusColor.STATUS_RED;
        } else {
          this.statusColor = StatusColor.STATUS_GREEN;
        }
        this.statusIcon = 'group';
      } else {
        this.status = 'service_number.no_agents';
        this.statusColor = StatusColor.STATUS_GRAY;
        this.statusIcon = 'block';
      }
    } else {
      this.status = 'service_number.service';
      this.statusColor = StatusColor.STATUS_GREEN;
      this.statusIcon = 'domain';
    }
  }
}
